import { BottomSheet } from "@clipboard-health/ui-components";
import { Text, type UseModalState } from "@clipboard-health/ui-react";
import { Box, DialogContent, Link } from "@mui/material";
import { useGetAttendanceScoreProfile } from "@src/appV2/AttendanceScore/api/useGetAttendanceScoreProfile";
import { CbhFeatureFlag, useCbhFlag } from "@src/appV2/FeatureFlags";
import { PRIORITY_ACCESS_COLOR } from "@src/appV2/lib/Theme/constants";
import { Button } from "@src/appV2/redesign/components/Button";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { format } from "date-fns";

interface ScoreNotificationDialogProps {
  modalState: UseModalState;
  clipboardScoreDelta: number;
}

export function ClipboardCashProgressBottomSheet({
  modalState,
  clipboardScoreDelta = 0,
}: ScoreNotificationDialogProps) {
  const { userId } = useDefinedWorker();
  const { data: attendanceScoreProfile } = useGetAttendanceScoreProfile(userId);
  const clipboardScoreReward = useCbhFlag(CbhFeatureFlag.CLIPBOARD_SCORE_REWARD, {
    defaultValue: {
      enabled: false,
    },
  });

  if (!clipboardScoreReward.enabled) {
    return null;
  }

  const userClipboardScore = attendanceScoreProfile?.clipboardScore ?? 0;
  const formattedEndDate = format(new Date(clipboardScoreReward.endTime), "MMMM d, yyyy");

  return (
    <BottomSheet
      modalState={modalState}
      footer={
        <DialogFooter orientation="vertical">
          <Button
            fullWidth
            variant="contained"
            sx={{
              backgroundColor: PRIORITY_ACCESS_COLOR,
              "&:hover": {
                backgroundColor: PRIORITY_ACCESS_COLOR,
                opacity: 0.9,
              },
            }}
            onClick={modalState.closeModal}
          >
            Got it
          </Button>
        </DialogFooter>
      }
    >
      <DialogContent>
        <Box sx={{ p: 2 }}>
          <Text variant="h2" sx={{ mb: 2, textAlign: "center" }}>
            Clipboard Cash
          </Text>

          {clipboardScoreDelta >= clipboardScoreReward.scoreDeltaNeededForReward ? (
            <Text variant="body1" sx={{ textAlign: "center" }}>
              Congrats! Your score is {userClipboardScore}, and you will earn $
              {clipboardScoreReward.rewardAmountInDollars} cash if you maintain it until{" "}
              {formattedEndDate}.{" "}
              <Link
                href="https://docs.google.com/document/d/1RQKnngFXm2IUftTRdm2iR-K8PiAMf_caNUAEM1eg32Y/edit?tab=t.0"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: PRIORITY_ACCESS_COLOR }}
              >
                Learn More
              </Link>
            </Text>
          ) : (
            <Text variant="body1" sx={{ textAlign: "center" }}>
              Your score is {userClipboardScore}. You need{" "}
              {clipboardScoreReward.scoreDeltaNeededForReward - clipboardScoreDelta} more points by{" "}
              {formattedEndDate} to earn ${clipboardScoreReward.rewardAmountInDollars} cash.{" "}
              <Link
                href="https://docs.google.com/document/d/1RQKnngFXm2IUftTRdm2iR-K8PiAMf_caNUAEM1eg32Y/edit?tab=t.0"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: PRIORITY_ACCESS_COLOR }}
              >
                Learn More
              </Link>
            </Text>
          )}
        </Box>
      </DialogContent>
    </BottomSheet>
  );
}
